import { useEffect, useRef } from 'react';

import { attachSnippetResize, createSnippetIframe } from './snippet-iframe';

/**
 * Type of component props.
 */
type SandboxedHtmlProps = {
  // raw html to render inside the sandboxed iframe
  html: string;
  // class name applied to the host element (carries any layout styling)
  className?: string;
  // names of host css custom properties (e.g. --omq-text-color) to capture from
  // the surrounding context and inject into the iframe; omit for a neutral frame
  themeVars?: readonly string[];
  // accessible name for the iframe (announced by screen readers); omit for none
  title?: string;
  // BCP47 language tag applied to the iframe document's <html lang>
  lang?: string;
};

/**
 * Renders raw html inside a sandboxed iframe (opaque origin, no same-origin
 * access). Scripts run but cannot reach the host page; height auto-sizes via a
 * postMessage handshake. Replaces ShadowHtml for html snippets.
 *
 * @param {SandboxedHtmlProps} props - component props
 *
 * @returns {JSX.Element}
 */
export function SandboxedHtml({
  html,
  className,
  themeVars,
  title,
  lang,
}: SandboxedHtmlProps): JSX.Element {
  // host element: iframe mount point and the source of inherited theme vars
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (host == null) {
      return undefined;
    }

    // capture requested theme vars from the host's computed style; custom
    // properties inherit across shadow boundaries, so this picks up the
    // surrounding chatbot/help theme
    let theme: Record<string, string> | undefined;
    if (themeVars && themeVars.length > 0) {
      const computed = getComputedStyle(host);
      const captured: Record<string, string> = {};
      themeVars.forEach((name) => {
        const value = computed.getPropertyValue(name).trim();
        if (value !== '') {
          captured[name] = value;
        }
      });
      theme = captured;
    }

    const { iframe, nonce } = createSnippetIframe(html, { theme, title, lang });
    const detach = attachSnippetResize(iframe, nonce);

    host.innerHTML = '';
    host.appendChild(iframe);

    return () => {
      detach();
      if (iframe.parentNode === host) {
        host.removeChild(iframe);
      }
    };
  }, [html, className, themeVars, title, lang]);

  return <div ref={hostRef} className={className} />;
}
